// Bối cảnh/thời đại ở cấp PLAN (đợt 260804) — hiện bản đang có hiệu lực, cho đè riêng cho tập này,
// và hiện băng cảnh báo khi chương đọc ra thời đại khác bản của truyện.
//
// 🔴 Băng cảnh báo là mắt xích cuối của cả chuỗi: log bị LOG_LEVEL=error nuốt ⇒ phải lưu vào DB ⇒ phải
// thêm kênh đọc-độc-lập để có gì mà báo. Không hiện ở đây thì cả chuỗi đó bằng không.
import { useBankErrorReporter } from './WorkflowRunContext'; // P9D #6 (I10)
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Collapse, Popconfirm, Space, Tag, Typography, message } from 'antd';
import { useFilmPlan } from './FilmPlanContext';
import { loadWorldAndStale } from './worldStaleLoad';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK } from './workflowAssetsPreload';
import { filmApi } from '../../services/api/film';
import type { ResolvedWorldSetting, StaleReport, WorldSetting } from '../../types/film';
import WorldSettingEditor from './WorldSettingEditor';
import WorldSettingView from './WorldSettingView';
import { describeSource, isEmptyWorldSetting, summarizeStale, describeAssetStatus } from './worldSettingUtils';

const { Text } = Typography;

const FilmWorldSettingPanel: React.FC = () => {
  const { planId, plan, reloadPlan } = useFilmPlan();
  const reportError = useBankErrorReporter();
  const [resolved, setResolved] = useState<ResolvedWorldSetting | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staleItems, setStaleItems] = useState<StaleReport['items']>([]);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async (first = false) => {
    if (!planId) return;
    // 261029 P1 B2c: hai nguồn khởi song song, fallback riêng từng nguồn (xem worldStaleLoad). P3 F1: chỉ lần nạp ĐẦU lấy từ chỗ giao một lần; làm tươi sau ghi gọi `load()` ⇒ gọi mới.
    const api = first === true ? { getWorldSetting: (id: string) => takeWorkflowHandoff(AK.world(id), () => filmApi.getWorldSetting(id)), getStaleAssets: (id: string) => takeWorkflowHandoff(AK.stale(id), () => filmApi.getStaleAssets(id)) } : filmApi;
    const out = await loadWorldAndStale(planId, api);
    setResolved(out.resolved);
    setStaleItems(out.staleItems);
  }, [planId]);

  useEffect(() => { load(true); setEditing(false); }, [load]);

  if (!planId) return null;

  const setting = resolved?.setting || null;
  const src = describeSource(resolved?.source);
  const warning: string | null = (plan as any)?.visualBible?.world_setting_warning || null;
  const stale = summarizeStale(staleItems);

  const save = async (next: WorldSetting | null) => {
    setSaving(true);
    try {
      setResolved(await filmApi.setWorldSetting(planId, next));
      message.success(next ? 'Saved episode-specific period' : 'Episode reverted to story period');
      setEditing(false);
      reloadPlan(); // cảnh báo nằm trong visual_bible ⇒ nạp lại plan để băng cảnh báo cập nhật
    } catch (e: any) {
      message.error(e?.message || 'Failed to save period');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Gen lại ảnh thuộc bối cảnh cũ. Không truyền `ids` ⇒ backend tự lấy MỌI mục STALE (không gồm UNKNOWN).
   * Bấm lẻ trên một mục thì truyền `includeUnknown` để gen được cả mục "không rõ" — người đã chủ động chọn.
   * Chạy nền ⇒ poll lại sau vài giây để bảng tự cập nhật, khỏi bắt người tự refresh.
   */
  const regen = async (ids?: string[]) => {
    setRegenerating(true);
    try {
      const r = await filmApi.regenerateStale(planId, ids ? { ids, includeUnknown: true } : {});
      if (!r.started) { message.info('Nothing to regenerate'); return; }
      message.success(`Regenerating ${r.total} asset(s) in the background…`);
      setTimeout(load, 20_000);
      setTimeout(load, 60_000);
    } catch (e: any) {
      reportError(e, 'Gen lại ảnh cũ', (err) => message.error(err?.message || 'Regeneration failed')); // không Provider ⇒ ĐÚNG câu cũ
    } finally {
      setRegenerating(false);
    }
  };

  /** Giữ nguyên bối cảnh, chỉ tắt cảnh báo — không tạo snapshot giả chỉ để dập một cái Alert. */
  const dismiss = async () => {
    setSaving(true);
    try {
      await filmApi.dismissWorldSettingWarning(planId);
      reloadPlan();
    } catch (e: any) {
      message.error(e?.message || 'Could not dismiss warning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Collapse
      size="small"
      style={{ marginBottom: 12 }}
      defaultActiveKey={warning ? ['ws'] : undefined}  // có cảnh báo thì mở sẵn, đừng bắt người ta đi tìm
      items={[{
        key: 'ws',
        label: (
          <Space size={6}>
            <Text style={{ fontSize: 13 }}>🏛 World & Period</Text>
            <Tag color={src.color}>{src.label}</Tag>
            {warning && <Tag color="warning">⚠ mismatch</Tag>}
            {stale.counts.stale > 0 && <Tag color="warning">{stale.counts.stale} outdated</Tag>}
          </Space>
        ),
        children: (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {warning && (
              <Alert
                type="warning" showIcon message="This episode's chapters read a different period"
                description={warning}
                action={
                  <Space direction="vertical" size={4}>
                    <Button size="small" onClick={() => setEditing(true)}>Edit period</Button>
                    <Button size="small" type="text" loading={saving} onClick={dismiss}>Reviewed, keep</Button>
                  </Space>
                }
              />
            )}

            {editing ? (
              <WorldSettingEditor value={setting} saving={saving} onSave={save} onCancel={() => setEditing(false)} />
            ) : isEmptyWorldSetting(setting) ? (
              <Text type="secondary">
                Not set — AI fills this in when a plan is created. Set it manually only if this episode needs a different period.
              </Text>
            ) : (
              <WorldSettingView value={setting} />
            )}

            {!editing && (
              <Space>
                <Button size="small" onClick={() => setEditing(true)}>
                  {src.canDetach || isEmptyWorldSetting(setting) ? 'Override for this episode' : 'Edit episode period'}
                </Button>
                {resolved?.source === 'PLAN' && (
                  <Button size="small" danger loading={saving} onClick={() => save(null)}>
                    Revert to story
                  </Button>
                )}
              </Space>
            )}

            {/* ── E5-P2: ảnh nào đang thuộc bối cảnh cũ ──
                Đặt ngay dưới khối bối cảnh vì đây là HỆ QUẢ của việc đổi bối cảnh — người vừa sửa xong
                thấy luôn, khỏi đi lục 4 tab bank. */}
            {stale.visible.length > 0 && (
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                <Space wrap size={6} style={{ marginBottom: 6 }}>
                  <Text strong style={{ fontSize: 12 }}>Assets by period</Text>
                  <Tag color="green">{stale.counts.ok} in period</Tag>
                  {stale.counts.stale > 0 && <Tag color="warning">{stale.counts.stale} outdated</Tag>}
                  {stale.counts.unknown > 0 && <Tag>{stale.counts.unknown} unknown</Tag>}
                  {stale.counts.stale > 0 && (
                    <Popconfirm
                      title={`Regenerate ${stale.counts.stale} outdated asset(s)?`}
                      description="Each asset is a paid generation. Runs in the background."
                      okText="Regenerate" cancelText="Cancel"
                      onConfirm={() => regen()}
                    >
                      <Button size="small" type="primary" loading={regenerating}>
                        Regenerate {stale.counts.stale} outdated
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
                {stale.visible.map((it) => {
                  const meta = describeAssetStatus(it.status);
                  return (
                    <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 2 }}>
                      <Tag color={meta.color} style={{ margin: 0, minWidth: 72, textAlign: 'center' }}>{meta.label}</Tag>
                      <Text type="secondary" style={{ minWidth: 64 }}>{it.kind}</Text>
                      <Text>{it.name}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {it.assetEra ? `(made under "${it.assetEra}")` : '(made before tracking)'}
                      </Text>
                      <Button size="small" type="link" loading={regenerating} onClick={() => regen([it.id])}>Regenerate</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Space>
        ),
      }]}
    />
  );
};

export default FilmWorldSettingPanel;
